/**
 * ORDER 31-S2 — THE APP-SIDE RANGE CURSOR CONTRACT (CHART C's backwards pager).
 *
 * ⛔ WHY THIS IS A THIRD MODULE AND NOT A WIDENING OF `cvdPaging.ts`. The two rails do not page
 * the same axis, and pretending otherwise is the bug that would ship. `/api/es/cvd` pages a
 * TIME GRID: its cursor must land on a bucket open, its left-edge trigger is measured in
 * milliseconds, and `rungSpanMs` tells the client how wide one bar is. A RANGE bar has no span —
 * it closes when PRICE has travelled N ticks — so CHART C draws on a SYNTHETIC SEQUENTIAL
 * timeline (`rangeChartModel.syntheticTime`) where a millisecond means nothing at all. A shared
 * trigger would either compare a wall-clock window against a synthetic axis (always true, paging
 * forever) or compare bar counts against a real one (never true, paging never). ⇒ ***THE UNIT OF
 * A PAGING TRIGGER IS THE UNIT OF THE AXIS IT WATCHES; CHART C's AXIS COUNTS BARS.***
 *
 * ⛔ THE PREPEND MUST MOVE THE CVD BASELINE, AND THAT IS THE ONE THING A NAIVE MERGE GETS WRONG.
 * `buildRangeChartModel` walks `cumulativeBaseline` forward through `contracts` to draw the CVD
 * line. That baseline is "the session CVD before the FIRST DRAWN BAR". Prepend 300 older bars and
 * leave the baseline where it was, and every CVD value on the chart is displaced by the sum of
 * the prepended contracts — a line with a plausible shape and a wrong level, which is exactly the
 * failure that looks like data rather than like a bug. `withRangeHistory` therefore replaces the
 * baseline with the OLDEST PAGE'S OWN `session_cvd_before`, which the Worker measured and stored.
 *
 * ⛔ AND PROVENANCE IS DATA, NEVER INK (R1106). A lake bar and a live bar CONFORM BY CONSTRUCTION —
 * S1's derivation is a pinned port of the Worker's own `buildRangeBars`, and the Worker's history
 * door re-derives `side`/`effort`/`absorbed` with transcribed arithmetic. There is therefore
 * nothing true to SAY about a lake bar that a viewer needs to see, and marking one would be a
 * visible claim of difference where none was measured. The counts ride `data-*` for the census;
 * the chart draws one series.
 */

import type { RangeChartBar } from "./lwc/rangeChartModel.ts";

/** The Worker's declared per-page ceiling (`MAX_RANGE_PAGE_LIMIT`). Asking past it is capped. */
export const RANGE_PAGE_LIMIT = 300;

/**
 * How close to the loaded edge a pan must come before the next page is worth fetching —
 * in BARS, because CHART C's x-axis is a bar index. See the header.
 */
export const RANGE_LEFT_EDGE_PREFETCH_BARS = 3;

/** The rungs `/api/es/cvd/range` serves. A pill outside this set has no door. */
export const RANGE_TICK_RUNGS = [8, 12] as const;

export interface RangePage {
  readonly bars: readonly RangeChartBar[];
  readonly root: string;
  readonly ticks: number;
  readonly hasMore: boolean;
  readonly nextBefore: number | null;
  readonly pageEmpty: boolean;
  /** The measured session CVD before this page's OLDEST bar. `null` on an empty page. */
  readonly sessionCvdBefore: number | null;
  /** `false` while the S1 DDL is unapplied — an honest EMPTY, not a fault. */
  readonly tablePresent: boolean;
}

export type RangePageRead =
  | { readonly page: RangePage; readonly blocked: null }
  | { readonly page: null; readonly blocked: string };

/** Only the declared rungs have a history door; a pill outside them must not issue a cursor. */
export function canPageRangeHistory(ticks: number): boolean {
  return (RANGE_TICK_RUNGS as readonly number[]).includes(ticks);
}

export function rangePageUrl(ticks: number, limit: number, before: number | null = null): string {
  if (!Number.isFinite(limit)) throw new RangeError(`rangePageUrl: limit must be finite, got ${limit}`);
  if (!canPageRangeHistory(ticks)) {
    throw new RangeError(`range history is served for ${RANGE_TICK_RUNGS.join("/")}t only, got ${ticks}t`);
  }
  const appliedLimit = Math.max(1, Math.min(RANGE_PAGE_LIMIT, Math.floor(limit)));
  const query = new URLSearchParams({ ticks: String(ticks), limit: String(appliedLimit) });
  if (before !== null) {
    if (!Number.isSafeInteger(before) || before < 946_684_800_000) {
      throw new RangeError(`before must be an integer epoch-ms cursor, got ${before}`);
    }
    // ⛔ THERE IS NO CLIENT-SIDE GRID RULE TO APPLY, AND THAT IS NOT AN OVERSIGHT. ORDER 44's
    // cursor must be bucket-aligned and `cvdPageUrl` still leaves that refusal to the Worker
    // (one authority per rule). A range cursor has NO grid to align to — `t0` lands wherever the
    // tape put it — so the only bound is the one below: it must be an epoch-ms integer the door
    // itself handed out. Inventing an alignment here would refuse every honest cursor.
    query.set("before", String(before));
  }
  return `/api/es/cvd/range?${query.toString()}`;
}

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Read one served history page, or REFUSE IT BY NAME.
 *
 * ⚠ THE RUNG AND ROOT CHECKS READ THE ECHO, NEVER THE REQUEST — `rangeRefusal` in the panel makes
 * the same call for the live leg, and for the same measured reason: a page reader that trusted
 * what it asked for would silently prepend 8t bars under a 12t label, drawing a history whose
 * geometry is wrong in a way no chip would report.
 *
 * ⛔ AN EMPTY PAGE IS A SUCCESS, NOT A REFUSAL. Running off the oldest end of the lake — and the
 * UNAPPLIED-DDL state, where the table does not exist yet — both arrive as `ok:true` with no bars.
 * The client must STOP PAGING, not render a fault: this route ships before the year-run, and a
 * chart that screamed on every pan until the desk applied the DDL would train the owner to ignore
 * the chip that will one day be real.
 */
export function readRangePage(body: unknown, requestedTicks: number, expectRoot = "ES"): RangePageRead {
  if (body === null || typeof body !== "object") {
    return { page: null, blocked: `${requestedTicks}t range history returned an unreadable payload` };
  }
  const raw = body as Record<string, unknown>;
  if (raw.ok !== true) {
    return {
      page: null,
      blocked: `${requestedTicks}t range history refused: ${String(raw.reason ?? raw.error ?? "no reason given")}`,
    };
  }
  if (raw.root !== expectRoot) {
    return {
      page: null,
      blocked: `range history served root=${String(raw.root)}, expected root=${expectRoot}; refusing to pool roots`,
    };
  }
  if (raw.ticks !== requestedTicks) {
    return {
      page: null,
      blocked: `range history served ${String(raw.ticks)}t, selected ${requestedTicks}t; refusing a mislabeled series`,
    };
  }
  if (!Array.isArray(raw.bars)) {
    return { page: null, blocked: `${requestedTicks}t range history payload has no bars array` };
  }
  const bars: RangeChartBar[] = [];
  for (const [index, value] of raw.bars.entries()) {
    if (value === null || typeof value !== "object") {
      return { page: null, blocked: `${requestedTicks}t range history bar ${index} is not an object` };
    }
    const row = value as Record<string, unknown>;
    // The four fields `buildRangeChartModel` walks. A bar missing one of them would draw as a
    // hole or a NaN, so it is refused at the door rather than nulled into the series.
    if (!finite(row.t0) || !finite(row.t1) || !finite(row.c) || !finite(row.contracts)) {
      return {
        page: null,
        blocked: `${requestedTicks}t range history bar ${index} is missing a finite t0/t1/c/contracts`,
      };
    }
    bars.push(value as unknown as RangeChartBar);
  }
  const nextBefore = raw.next_before == null
    ? null
    : finite(raw.next_before) && Number.isSafeInteger(raw.next_before) ? raw.next_before : Number.NaN;
  if (Number.isNaN(nextBefore)) {
    return { page: null, blocked: `${requestedTicks}t range history served an invalid next_before cursor` };
  }
  const hasMore = raw.has_more === true;
  if (hasMore && nextBefore === null) {
    return { page: null, blocked: `${requestedTicks}t range history claims has_more without next_before` };
  }
  const baseline = finite(raw.session_cvd_before) ? raw.session_cvd_before : null;
  if (bars.length > 0 && baseline === null) {
    return {
      page: null,
      blocked: `${requestedTicks}t range history served bars without the session CVD baseline they must be drawn against`,
    };
  }
  return {
    page: {
      bars,
      root: expectRoot,
      ticks: requestedTicks,
      hasMore,
      nextBefore,
      pageEmpty: raw.page_empty === true || bars.length === 0,
      sessionCvdBefore: baseline,
      tablePresent: raw.table_present !== false,
    },
    blocked: null,
  };
}

/**
 * Prepend older bars, de-duplicating the seam. Keyed on `t0` with the CURRENT page winning —
 * the same direction `mergeCvdPages` chose, for the same reason: the live poll is the authority
 * on any bar they both carry, and the newest bar of a live window can still be re-derived as the
 * tape fills in behind it.
 */
export function mergeRangePages(
  older: readonly RangeChartBar[],
  current: readonly RangeChartBar[],
): RangeChartBar[] {
  const byOpen = new Map<number, RangeChartBar>();
  for (const bar of older) byOpen.set(bar.t0, bar);
  for (const bar of current) byOpen.set(bar.t0, bar);
  return [...byOpen.values()].sort((a, b) => a.t0 - b.t0);
}

/** The two fields a prepend changes on CHART C's feed, and no others. */
export interface RangeDrawable {
  /** A fresh array in both branches — the caller owns it and the panel's `$state` may hold it. */
  readonly bars: RangeChartBar[];
  readonly baseline: number | null;
}

/**
 * The bars and the baseline CHART C should draw, with history in front of the live window.
 *
 * ⛔ ONLY THESE TWO MOVE. `age_s`, `stale` and `state` describe the NEWEST data the panel holds and
 * a history page's own (legitimately months-old) age must never reach the STALENESS chip — the
 * ORDER 44 ruling, applied to CHART C. ⇒ ***A PREPEND EXTENDS THE SERIES, IT DOES NOT RE-DATE
 * THE FEED.*** The baseline is the exception precisely because it is NOT a freshness fact: it is
 * the CVD level of whichever bar is drawn first, so it moves with the series or the line is wrong.
 */
export function withRangeHistory(
  liveBars: readonly RangeChartBar[],
  liveBaseline: number | null,
  older: readonly RangeChartBar[],
  olderBaseline: number | null,
): RangeDrawable {
  if (older.length === 0) return { bars: [...liveBars], baseline: liveBaseline };
  return {
    bars: mergeRangePages(older, liveBars),
    baseline: olderBaseline ?? liveBaseline,
  };
}

/**
 * The left-edge decision, read off the LOGICAL range because CHART C's axis is a bar index.
 *
 * LWC reports a logical `from` that goes NEGATIVE once the viewport is panned past the first bar,
 * so `<= PREFETCH` is true both approaching the edge and beyond it. `loadedBars === 0` is false —
 * there is no edge to approach until something is drawn, and a trigger that fired on an empty
 * chart would page history for a chart with no live window to attach it to.
 */
export function shouldLoadOlderRange(
  logical: { from: number; to: number } | null,
  loadedBars: number,
  ticks: number,
): boolean {
  if (logical === null || loadedBars <= 0 || !canPageRangeHistory(ticks)) return false;
  if (!Number.isFinite(logical.from)) return false;
  return logical.from <= RANGE_LEFT_EDGE_PREFETCH_BARS;
}

/**
 * ORDER 82 — THE CURSOR THAT OPENS THE PANEL ON THE LAKE WHEN THE LIVE EDGE IS EMPTY.
 *
 * ⛔⛔ MEASURED AT THE DESK'S HAND 18:15Z: `/api/es/cvd/range?ticks=8&limit=300` answers
 * `state: EMPTY` DURING RTH, while `before=<now>` serves 300 lake bars. The live leg reads the last
 * min(4320, limit×16) TRADES since the session start and builds bars in TS, so in a quiet stretch
 * 4,320 trades never travel 8 ticks and **the live page is empty by construction, not by fault**.
 *
 * ⛔ AND THE PANEL THEN DREW NOTHING AT ALL, WITH 247,162 LAKE BARS BEHIND IT. `loadRange` seeded
 * the history cursor ONLY from `liveBars[0].t0`, so an empty live page left the cursor `null`,
 * `rangeHasMore` false, and the pager could never fire. *** A CURSOR DERIVED SOLELY FROM THE LIVE
 * WINDOW CANNOT REACH HISTORY ON EXACTLY THE DAYS HISTORY IS ALL THERE IS. ***
 *
 * `now` is the seed because the history door is exclusive-before: asking for "bars before now"
 * is the whole lake, newest first, which is precisely the first page the owner should see.
 *
 * ⚠ ONLY AT `historyDepth === 0`. Once a page has landed, the cursor belongs to the pager and
 * re-seeding it from the clock would re-serve the newest page forever, pinning the owner to the
 * lake's right edge while looking like a chart that refuses to pan.
 */
export function historyFirstCursor(
  liveBarCount: number,
  historyDepth: number,
  ticks: number,
  now: number,
): number | null {
  if (liveBarCount > 0) return null;          // the live edge exists; its own bar seeds the cursor
  if (historyDepth > 0) return null;          // the pager owns the cursor from here
  if (!canPageRangeHistory(ticks)) return null;
  if (!Number.isFinite(now)) return null;
  return now;
}

/**
 * ORDER 82 — whether the LIVE EDGE ABSENT pill is on the ink.
 *
 * ⛔ IT IS A PROVENANCE STATEMENT, NOT AN ERROR. The lake bars on screen are real and correct;
 * what is absent is the live edge in front of them. It is muted, never red — an operator who is
 * told something is broken when it is merely quiet learns to ignore the ink (R1106).
 *
 * ⛔ AND IT MUST NOT SHOW ON AN EMPTY CHART. With nothing drawn there is no provenance to qualify;
 * the panel's own RANGE FEED EMPTY line already speaks. The pill claims "what you see is lake",
 * which is only true once something is seen.
 */
export function liveEdgeAbsent(liveBarCount: number, drawnBarCount: number): boolean {
  return liveBarCount === 0 && drawnBarCount > 0;
}

/** The pill's words. `lake to <t1 UTC>` names the NEWEST drawn bar — the edge of what is real. */
export function liveEdgeAbsentLabel(newestDrawnT1Ms: number | null): string {
  if (newestDrawnT1Ms === null || !Number.isFinite(newestDrawnT1Ms)) return "LIVE EDGE ABSENT";
  return `LIVE EDGE ABSENT · lake to ${new Date(newestDrawnT1Ms).toISOString().slice(0, 16).replace("T", " ")}Z`;
}

/** What `loadRange` must do on an open, whatever the live leg served. See `rangeOpenPlan`. */
export interface RefusedLiveLegPlan {
  /** Seed the history cursor with this, or `null` to leave the cursor alone. */
  readonly cursor: number | null;
  /** Whether the pager may fire — meaningful only when `cursor` is non-null. */
  readonly hasMore: boolean;
  /** Pull ONE page now, so the panel opens on the lake without a gesture. */
  readonly fetchOnce: boolean;
  /** Rebuild the model. ⛔ FALSE MEANS "NULL THE MODEL", which BLANKS THE INK. */
  readonly rebuild: boolean;
}

/**
 * ORDER 82 fix-first (GK-bq 578b532c) — THE WHOLE REFUSED-BRANCH DECISION, IN ONE PLACE.
 *
 * ⛔⛔ THE DEFECT THIS EXISTS TO KILL: THE PANEL OPENED ON THE LAKE AND BLANKED 60 s LATER.
 * My first cure kept `bars` alive across a refused live leg and then set `model = null`, with
 * `rebuildModel()` reachable ONLY inside the seed branch. The seed is null once `historyDepth > 0`
 * (seeded once — correct), so on the next refresh nothing rebuilt the model, and `feedSeries()`
 * clears every series on `!model`. The bars survived in state; THE INK DID NOT. The owner was left
 * with an empty chart still wearing the LIVE EDGE ABSENT pill, on every refused tick until a pan
 * or a live bar.
 * *** THE SERIES ARE FED FROM `model`, NOT FROM `bars`. I VERIFIED THE DATA PATH AND CALLED THE
 * RENDER PATH CURED — the same half-measurement that shipped INT-282's inert pill the day before. ***
 *
 * ⭐ WHY A REDUCER AND NOT THREE MORE `if`s: every one of these four outputs was previously an
 * inline statement in an async Svelte branch, where no test could reach it — GK-bq's M1, M2, M3
 * and M6 all survived a 15/15 suite for exactly that reason. As one pure function the decision is
 * driven by fixtures; what remains in the panel is assignment, which its own arms and mutants
 * cover. A decision you cannot call is a decision you cannot arm.
 *
 * `heldBars` is what is DRAWN (`bars.length`), not what was fetched: the question is whether
 * anything is on the ink to preserve.
 */
export function rangeOpenPlan(
  liveBarCount: number,
  firstLiveT0: number | null,
  heldBars: number,
  historyDepth: number,
  ticks: number,
  now: number,
): RefusedLiveLegPlan {
  // ⛔⛔ ORDER 82 r2 (desk [GO] on f4f6cc81) — HISTORY FIRST **ALWAYS** ON OPEN, NOT ONLY WHEN EMPTY.
  // r1 fired only on an EMPTY live leg, because the premise was measured at 18:15Z 09-03 when the
  // route answered EMPTY through RTH. By 10:55Z 09-04 the same route served SEVEN bars — so the
  // refusal was null, the cure never fired, and the owner still saw a near-empty CHART C with a
  // six-day hole behind it. The desk's own eyes-on read the same thing: "seven live bars and a
  // flat line before them, no lake page."
  // *** A TRIGGER KEYED TO ONE MEASURED SYMPTOM EXPIRES WHEN THE SYMPTOM MOVES. *** The panel's
  // real requirement was never "rescue the empty case" — it is "open on something worth reading",
  // and a sparse live edge fails that exactly as an empty one does.
  //
  // The cursor is the OLDEST LIVE BAR'S OPEN when there is a live leg (ORDER 31-S2's rule: the
  // history door is exclusive-before, so the seam cannot repeat the bar that named it), and the
  // CLOCK when there is not.
  // ⚠ ONE RETURN, ONE `rebuild`. An earlier cut had two exits, each with its own rebuild
  // expression — and a mutant on the second one SURVIVED, because every case that reached it also
  // set `fetchOnce`. Two spellings of one rule are two things to arm and one of them will be
  // vacuous; the duplicate is why the mutant could not bite.
  const canSeed = historyDepth === 0 && canPageRangeHistory(ticks);
  const cursor = !canSeed
    ? null                                    // the pager owns the cursor once a page has landed
    : liveBarCount > 0
      ? (Number.isFinite(firstLiveT0 ?? NaN) ? firstLiveT0 : null)
      : historyFirstCursor(0, historyDepth, ticks, now);
  const fetchOnce = cursor !== null;
  return {
    cursor,
    hasMore: fetchOnce,
    fetchOnce,
    // ⛔ REBUILD WHENEVER THERE IS, OR IS ABOUT TO BE, SOMETHING DRAWN — held lake bars (the 60 s
    // refresh), a live leg, or a page on its way. Only a panel with none of the three may null the
    // model, and then the empty ink is the honest answer.
    rebuild: heldBars > 0 || liveBarCount > 0 || fetchOnce,
  };
}

/**
 * ORDER 82 r2 — THE HISTORY GAP PILL: the live leg does not reach the lake.
 *
 * ⛔ MEASURED 10:55Z 09-04: the lake's newest bar is 2026-08-28 20:45Z and the live leg begins
 * 2026-09-04 09:27Z — a SIX-DAY HOLE that no amount of paging closes, because the bars were never
 * written. Drawing both legs without saying so would present one continuous series across a gap
 * the data does not have, which is a lying artefact even though every bar in it is real.
 * ⚠ Muted, like its sibling: the hole is ORDER 79-A's to close and nothing here is broken.
 */
export function historyGapLabel(
  newestLakeT1: number | null,
  oldestLiveT0: number | null,
): string | null {
  if (newestLakeT1 === null || oldestLiveT0 === null) return null;
  if (!Number.isFinite(newestLakeT1) || !Number.isFinite(oldestLiveT0)) return null;
  if (newestLakeT1 >= oldestLiveT0) return null;      // the legs meet: no gap to declare
  return `HISTORY GAP · lake to ${new Date(newestLakeT1).toISOString().slice(0, 10)}`;
}
